import { type DefaultContext, gqlPromise, mutationPromise, http } from '@egw/clients'
import { type QueryClient } from '@tanstack/react-query'
import { createMutationHook } from '../../../hooks'
import {
  invalidateAchTransferQueries,
  invalidateBankInvestmentTransferQueries,
  invalidateInternalBankTransferQueries,
  invalidateInvestmentAccountUpdateQueries,
} from '../../../utils'
import {
  type submitWireInputV3,
  type BankingInstitution,
  type CreateAchOrderInputV2,
  type CreateAchOrderOutput,
  type CreateBankInternalTransferInput,
  type CreateBankInternalTransferOutput,
  type CreateBankInvestmentTransferInput,
  type CreateBankInvestmentTransferOutput,
  type GetBankInstitutionNameError,
  type PendingWireInput,
  type PendingWireInputV2,
  type PendingWireOutput,
  type SubmitWireInput,
  type SubmitWireInputV2,
  type SubmitWireOutput,
  type Wire,
  type SubmitWireOutputV3,
  type FileUploadSuccess,
  type FileUploadError,
  type SubmitAcatDocumentsInput,
  type SubmitAcatDocumentsOutput,
  type AuthorizeJointAcatTransferInput,
  type ReviewJointAcatTransferOutput,
  type RejectJointAcatTransferInput,
  type PendingJointAcatTransfersOutput,
  type CreateInvestmentAchTransferInput,
  type CreateInvestmentAchTransferOutput,
  type SubmitInvestmentWireOutput,
  type SubmitInvestmentWireInput,
} from '../../../models'
import { WIRE_FRAGMENT } from './fragments'

/**
 * Get ACH institution name
 * @param routingNumber - routing number of the bank
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns bank name or error
 * @example const response = await getAchInstitutionName('123123123);
 */
export const getAchInstitutionName = async (
  routingNumber: string,
  context?: DefaultContext,
): Promise<BankingInstitution | GetBankInstitutionNameError> => {
  const response = (await gqlPromise(
    {
      name: 'achInstitutionName',
      query: `
        query achInstitutionName($getBankInstitutionNameInput: GetBankInstitutionNameInput!) {
          achInstitutionName(getBankInstitutionNameInput: $getBankInstitutionNameInput) {
            ... on BankingInstitution {
            __typename
              bankName
            }
            ... on GetBankInstitutionNameError {
            __typename
              type
            }
          }
        }
      `,
    },
    {
      getBankInstitutionNameInput: {
        routingNumber,
      },
    },
    context,
  )) as { achInstitutionName: BankingInstitution | GetBankInstitutionNameError }
  return response.achInstitutionName
}

/**
 * Get Wire transfer institution name
 * @param routingNumber - routing number of the bank
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns bank name or error
 * @example const response = await getWireInstitutionName('123123123);
 */
export const getWireInstitutionName = async (
  routingNumber: string,
  context?: DefaultContext,
): Promise<BankingInstitution | GetBankInstitutionNameError> => {
  const response = (await gqlPromise(
    {
      name: 'wireInstitutionName',
      query: `
        query wireInstitutionName($getBankInstitutionNameInput: GetBankInstitutionNameInput!) {
          wireInstitutionName(getBankInstitutionNameInput: $getBankInstitutionNameInput) {
            ... on BankingInstitution {
              __typename
              bankName
            }
            ... on GetBankInstitutionNameError {
            __typename
              type
            }
          }
        }
      `,
    },
    {
      getBankInstitutionNameInput: {
        routingNumber,
      },
    },
    context,
  )) as { wireInstitutionName: BankingInstitution | GetBankInstitutionNameError }
  return response.wireInstitutionName
}

/**
 * Create ACH order
 * @param createAchOrderInputV2 - input for creating an ACH order
 * @param context - reference to the AbortController used to abort the call (optional)
 * @param queryClient - TanStack Query client instance for automatic cache invalidation (optional)
 * @returns CreateAchOrderSuccess | CreateAchOrderError | CreateAchOrderLimitError | BankingMinimumDepositNotMetError
 */
export const createAchOrderV2 = async (
  createAchOrderInputV2: CreateAchOrderInputV2,
  context?: DefaultContext,
  queryClient?: QueryClient,
): Promise<CreateAchOrderOutput> => {
  const response = (await mutationPromise(
    {
      name: 'createAchOrderV2',
      query: `mutation createAchOrderV2($createAchOrderInputV2: CreateAchOrderInputV2!) {
          createAchOrderV2(createAchOrderInputV2: $createAchOrderInputV2) {
            __typename
            ... on CreateAchOrderSuccess {
              id
            }
            ... on CreateAchOrderError {
              type
            }
            ... on CreateAchOrderLimitError {
              limitType
              limit
              remaining
            }
            ... on BankingMinimumDepositNotMetError {
              minimumDepositRequirementAmount
            }
          }
        }
      `,
    },
    {
      createAchOrderInputV2,
    },
    context,
  )) as { createAchOrderV2: CreateAchOrderOutput }

  const result = response.createAchOrderV2

  // Invalidate queries on successful ACH order creation if queryClient is provided
  if (queryClient && result.__typename === 'CreateAchOrderSuccess') {
    invalidateAchTransferQueries({
      client: queryClient,
      bankAccountAlias: createAchOrderInputV2.accountAlias,
      refetchType: 'all',
    })
  }

  return result
}

/**
 * Create ACH order hook with automatic QueryClient injection
 */
export const useCreateAchOrderV2 = createMutationHook<CreateAchOrderInputV2, CreateAchOrderOutput>(createAchOrderV2)

/**
 * Create bank investment transfer
 * @param createBankInvestmentTransferInput - input for creating a bank investment transfer
 * @param context - reference to the AbortController used to abort the call (optional)
 * @param queryClient - TanStack Query client instance for automatic cache invalidation (optional)
 * @returns CreateBankInvestmentTransfer | CreateBankInvestmentTransferError | InvestmentMinimumDepositNotMetError | AnnualRetirementContributionExceededError
 */
export const createBankInvestmentTransfer = async (
  createBankInvestmentTransferInput: CreateBankInvestmentTransferInput,
  context?: DefaultContext,
  queryClient?: QueryClient,
): Promise<CreateBankInvestmentTransferOutput> => {
  const response = (await mutationPromise(
    {
      name: 'createBankInvestmentTransfer',
      query: `mutation createBankInvestmentTransfer($createBankInvestmentTransferInput: CreateBankInvestmentTransferInput!) {
          createBankInvestmentTransfer(createBankInvestmentTransferInput: $createBankInvestmentTransferInput) {
            __typename
            ... on CreateBankInvestmentTransfer {
              id
              marketStatus
            }
            ... on CreateBankInvestmentTransferError {
              errorType
            }
            ... on InvestmentMinimumDepositNotMetError {
              minimumDepositRequirementAmount
            }
            ... on AnnualRetirementContributionExceededError {
              retirementContribution {
                annualContributionsLimit
                remainingAnnualLimit
              }
            }
          }
        }
      `,
    },
    {
      createBankInvestmentTransferInput,
    },
    context,
  )) as { createBankInvestmentTransfer: CreateBankInvestmentTransferOutput }

  const result = response.createBankInvestmentTransfer

  // Invalidate queries on successful bank investment transfer creation if queryClient is provided
  if (queryClient && result.__typename === 'CreateBankInvestmentTransfer') {
    invalidateBankInvestmentTransferQueries({
      client: queryClient,
      bankAccountAlias: createBankInvestmentTransferInput.bankAccountAlias.alias,
      investmentAccountAlias: createBankInvestmentTransferInput.investmentAccountAlias.alias,
      refetchType: 'all',
    })
  }

  return result
}

/**
 * Create bank investment transfer hook with automatic QueryClient injection
 */
export const useCreateBankInvestmentTransfer = createMutationHook<
  CreateBankInvestmentTransferInput,
  CreateBankInvestmentTransferOutput
>(createBankInvestmentTransfer)

/**
 * Create bank internal transfer
 * @param createBankInternalTransferInput - input for creating a bank internal transfer
 * @param context - reference to the AbortController used to abort the call (optional)
 * @param queryClient - TanStack Query client instance for automatic cache invalidation (optional)
 * @returns CreateBankInternalTransfer | CreateBankInternalTransferError
 */
export const createBankInternalTransfer = async (
  createBankInternalTransferInput: CreateBankInternalTransferInput,
  context?: DefaultContext,
  queryClient?: QueryClient,
): Promise<CreateBankInternalTransferOutput> => {
  const response = (await mutationPromise(
    {
      name: 'createBankInternalTransfer',
      query: `mutation createBankInternalTransfer($createBankInternalTransferInput: CreateBankInternalTransferInput!) {
          createBankInternalTransfer(createBankInternalTransferInput: $createBankInternalTransferInput) {
            __typename
            ... on CreateBankInternalTransfer {
              id
            }
            ... on CreateBankInternalTransferError {
              errorType
            }
          }
        }
      `,
    },
    {
      createBankInternalTransferInput,
    },
    context,
  )) as { createBankInternalTransfer: CreateBankInternalTransferOutput }

  const result = response.createBankInternalTransfer

  // Invalidate queries on successful bank internal transfer creation if queryClient is provided
  if (queryClient && result.__typename === 'CreateBankInternalTransfer') {
    invalidateInternalBankTransferQueries({
      client: queryClient,
      fromBankAccountAlias: createBankInternalTransferInput.fromAlias,
      toBankAccountAlias: createBankInternalTransferInput.toAlias,
      refetchType: 'all',
    })
  }

  return result
}

/**
 * Create bank internal transfer hook with automatic QueryClient injection
 */
export const useCreateBankInternalTransfer = createMutationHook<
  CreateBankInternalTransferInput,
  CreateBankInternalTransferOutput
>(createBankInternalTransfer)

/**
 * Get wire transfer by id
 * @param id - wire transfer id
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns wire transfer details
 * @example const response = await getWire('123123123);
 */
export const getWire = async (id: string, context?: DefaultContext): Promise<Wire> => {
  const response = (await gqlPromise(
    {
      name: 'wire',
      query: `
        query wire($id: ID!) {
          wire(id: $id) {
            ...WireFragment
          }
        }
        ${WIRE_FRAGMENT}
      `,
    },
    {
      id,
    },
    context,
  )) as { wire: Wire }
  return response.wire
}

export const getPendingJointAcatTransfers = async (
  context?: DefaultContext,
): Promise<PendingJointAcatTransfersOutput> => {
  const response = (await gqlPromise(
    {
      name: 'pendingJointAcatTransfers',
      query: `query pendingJointAcatTransfers {
        pendingJointAcatTransfers {
          pendingJointAcatTransfers {
            pendingAcatTransferAlias
            jointAccountHolderFirstName
            submittedFileNames
            transferType
            toAccountAlias
            status
          }
        }
      }`,
    },
    context,
  )) as { pendingJointAcatTransfers: PendingJointAcatTransfersOutput }
  return response.pendingJointAcatTransfers
}

/**
 * Submit wire transfer
 * @param submitWireInput - input for submitting a wire transfer
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Wire | SubmitWireError
 */
export const submitWire = async (
  submitWireInput: SubmitWireInput,
  context?: DefaultContext,
): Promise<SubmitWireOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitWire',
      query: `mutation submitWire($submitWireInput: SubmitWireInput!) {
          submitWire(submitWireInput: $submitWireInput) {
            __typename
            ... on Wire {
              ...WireFragment
            }
            ... on SubmitWireError {
              errorType
            }
          }
        }
        ${WIRE_FRAGMENT}
      `,
    },
    {
      submitWireInput,
    },
    context,
  )) as { submitWire: SubmitWireOutput }
  return response.submitWire
}

/**
 * Submit wire transfer hook
 */
export const useSubmitWire = createMutationHook<SubmitWireInput, SubmitWireOutput>(submitWire)

/**
 * Submit wire transfer V2
 * @param submitWireInputV2 - input for submitting a wire transfer
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Wire | SubmitWireError
 */
export const submitWireV2 = async (
  submitWireInputV2: SubmitWireInputV2,
  context?: DefaultContext,
): Promise<SubmitWireOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitWireV2',
      query: `mutation submitWireV2($submitWireInputV2: SubmitWireInputV2!) {
          submitWireV2(submitWireInputV2: $submitWireInputV2) {
            __typename
            ... on Wire {
              ...WireFragment
            }
            ... on SubmitWireError {
              errorType
            }
          }
        }
        ${WIRE_FRAGMENT}
      `,
    },
    {
      submitWireInputV2,
    },
    context,
  )) as { submitWireV2: SubmitWireOutput }
  return response.submitWireV2
}

/**
 * Submit wire transfer hook
 */
export const useSubmitWireV2 = createMutationHook<SubmitWireInputV2, SubmitWireOutput>(submitWireV2)

/**
 * Submit wire transfer V3
 * @param submitWireInputV3 - input for submitting a wire transfer V3 (APEX)
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Wire | SubmitWireError
 */
export const submitWireV3 = async (
  submitWireInputV3: submitWireInputV3,
  context?: DefaultContext,
): Promise<SubmitWireOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitWireV3',
      query: `mutation submitWireV3($submitWireInputV3: SubmitWireInputV3!) {
          submitWireV3(submitWireInputV3: $submitWireInputV3) {
            __typename
            ... on Wire {
              __typename
              isSubmitted
            }
            ... on SubmitWireError {
              errorType
            }
          }
        }
      `,
    },
    {
      submitWireInputV3,
    },
    context,
  )) as { submitWireV3: SubmitWireOutput }
  return response.submitWireV3
}

/**
 * Submit wire transfer hook
 */
export const useSubmitWireV3 = createMutationHook<submitWireInputV3, SubmitWireOutputV3>(submitWireV3)

/**
 * Pending wire transfer
 * @param pendingWireInput - input for pending wire transfer
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Wire | PendingWireError
 */
export const pendingWire = async (
  pendingWireInput: PendingWireInput,
  context?: DefaultContext,
): Promise<PendingWireOutput> => {
  const response = (await mutationPromise(
    {
      name: 'pendingWire',
      query: `mutation pendingWire($pendingWireInput: PendingWireInput!) {
          pendingWire(pendingWireInput: $pendingWireInput) {
            __typename
            ... on Wire {
              ...WireFragment
            }
            ... on PendingWireError {
              errorType
            }
          }
        }
        ${WIRE_FRAGMENT}
      `,
    },
    {
      pendingWireInput,
    },
    context,
  )) as { pendingWire: PendingWireOutput }
  return response.pendingWire
}

/**
 * Pending wire transfer hook
 */
export const usePendingWire = createMutationHook<PendingWireInput, PendingWireOutput>(pendingWire)

/**
 * Pending wire transfer V2
 * @param pendingWireInputV2 - input for pendingting a wire transfer
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Wire | PendingWireError
 */
export const pendingWireV2 = async (
  pendingWireInputV2: PendingWireInputV2,
  context?: DefaultContext,
): Promise<PendingWireOutput> => {
  const response = (await mutationPromise(
    {
      name: 'pendingWireV2',
      query: `mutation pendingWireV2($pendingWireInputV2: PendingWireInputV2!) {
          pendingWireV2(pendingWireInputV2: $pendingWireInputV2) {
            __typename
            ... on Wire {
              ...WireFragment
            }
            ... on PendingWireError {
              errorType
            }
          }
        }
        ${WIRE_FRAGMENT}
      `,
    },
    {
      pendingWireInputV2,
    },
    context,
  )) as { pendingWireV2: PendingWireOutput }
  return response.pendingWireV2
}

/**
 * Pending wire transfer hook
 */
export const usePendingWireV2 = createMutationHook<PendingWireInputV2, PendingWireOutput>(pendingWireV2)

/**
 * Upload manual review document
 * @param applicationId - application id
 * @param file - file to upload
 * @returns FileUploadSuccess | FileUploadError
 */
export const uploadManualReviewDocument = async (
  applicationId: string,
  file: File,
): Promise<FileUploadSuccess | FileUploadError> => {
  const formData = new FormData()
  formData.append('file', file)
  return http.post(`/enrollmentApplication/${applicationId}/manual-review/document`, formData) as Promise<
    FileUploadSuccess | FileUploadError
  >
}

/**
 * Upload ACAT document
 * @param file - file to upload
 * @returns FileUploadSuccess | FileUploadError
 */
export const uploadAcatDocument = async (file: File): Promise<FileUploadSuccess | FileUploadError> => {
  const formData = new FormData()
  formData.append('file', file)
  return http.post('/v1/investment/transfer/acat/document', formData) as Promise<FileUploadSuccess | FileUploadError>
}

/**
 * Submit ACAT documents
 * @param submitAcatDocumentsInput - input for submitting ACAT documents
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns SubmitAcatDocumentsSuccess | SubmitAcatDocumentsError
 */
export const submitAcatDocuments = async (
  submitAcatDocumentsInput: SubmitAcatDocumentsInput,
  context?: DefaultContext,
): Promise<SubmitAcatDocumentsOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitAcatDocuments',
      query: `mutation submitAcatDocuments($submitAcatDocumentsInput: SubmitAcatDocumentsInput!) {
          submitAcatDocuments(submitAcatDocumentsInput: $submitAcatDocumentsInput) {
            __typename
            ... on SubmitAcatDocumentsSuccess {
              isSuccessful
              pendingJointAcatTransfer {
                pendingAcatTransferAlias
                jointAccountHolderFirstName
                submittedFileNames
                transferType
                toAccountAlias
                status
              }
            }
            ... on SubmitAcatDocumentsError {
              errorType
            }
          }
        }
      `,
    },
    {
      submitAcatDocumentsInput,
    },
    context,
  )) as { submitAcatDocuments: SubmitAcatDocumentsOutput }
  return response.submitAcatDocuments
}

export const useSubmitAcatDocuments = createMutationHook<SubmitAcatDocumentsInput, SubmitAcatDocumentsOutput>(
  submitAcatDocuments,
)

/**
 * Authorize joint ACAT transfer
 * @param authorizeJointAcatTransferInput - input for authorizing a joint ACAT transfer
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns ReviewJointAcatTransferSuccess | ReviewJointAcatTransferError
 */
export const authorizeJointAcatTransfer = async (
  authorizeJointAcatTransferInput: AuthorizeJointAcatTransferInput,
  context?: DefaultContext,
): Promise<ReviewJointAcatTransferOutput> => {
  const response = (await mutationPromise(
    {
      name: 'authorizeJointAcatTransfer',
      query: `mutation authorizeJointAcatTransfer($authorizeJointAcatTransferInput: AuthorizeJointAcatTransferInput!) {
          authorizeJointAcatTransfer(authorizeJointAcatTransferInput: $authorizeJointAcatTransferInput) {
            __typename
            ... on ReviewJointAcatTransferSuccess {
              __typename
              isSuccessful
            }
            ... on ReviewJointAcatTransferOutputError {
              __typename
              errorType
            }
          }
        }
      `,
    },
    {
      authorizeJointAcatTransferInput,
    },
    context,
  )) as { authorizeJointAcatTransfer: ReviewJointAcatTransferOutput }
  return response.authorizeJointAcatTransfer
}

export const useAuthorizeJointAcatTransfer = createMutationHook<
  AuthorizeJointAcatTransferInput,
  ReviewJointAcatTransferOutput
>(authorizeJointAcatTransfer)

/**
 * Reject joint ACAT transfer
 * @param rejectJointAcatTransferInput - input for rejecting a joint ACAT transfer
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns ReviewJointAcatTransferSuccess | ReviewJointAcatTransferError
 */
export const rejectJointAcatTransfer = async (
  rejectJointAcatTransferInput: RejectJointAcatTransferInput,
  context?: DefaultContext,
): Promise<ReviewJointAcatTransferOutput> => {
  const response = (await mutationPromise(
    {
      name: 'rejectJointAcatTransfer',
      query: `mutation rejectJointAcatTransfer($rejectJointAcatTransferInput: RejectJointAcatTransferInput!) {
        rejectJointAcatTransfer(rejectJointAcatTransferInput: $rejectJointAcatTransferInput) {
          ... on ReviewJointAcatTransferSuccess {
            __typename
            isSuccessful
          }
          ... on ReviewJointAcatTransferOutputError {
            __typename
            errorType
          }
        }
      }
      `,
    },
    {
      rejectJointAcatTransferInput,
    },
    context,
  )) as { rejectJointAcatTransfer: ReviewJointAcatTransferOutput }
  return response.rejectJointAcatTransfer
}

export const useRejectJointAcatTransfer = createMutationHook<
  RejectJointAcatTransferInput,
  ReviewJointAcatTransferOutput
>(rejectJointAcatTransfer)

/**
 * Create Investment | ACH Transfer
 * @param CreateInvestmentAchTransferInput - input for creating an investment ACH transfer
 * @param context - reference to the AbortController used to abort the call (optional)
 * @param queryClient - TanStack Query client instance for automatic cache invalidation (optional)
 * @return CreateInvestmentAchTransfer | CreateInvestmentAchTransferError | AnnualRetirementContributionExceededError
 */

export const createInvestmentAchTransfer = async (
  createInvestmentAchTransferInput: CreateInvestmentAchTransferInput,
  context?: DefaultContext,
  queryClient?: QueryClient,
): Promise<CreateInvestmentAchTransferOutput> => {
  const response = (await mutationPromise(
    {
      name: 'createInvestmentAchTransfer',
      query: `mutation createInvestmentAchTransfer($createInvestmentAchTransferInput: CreateInvestmentAchTransferInput!) {
      createInvestmentAchTransfer(createInvestmentAchTransferInput: $createInvestmentAchTransferInput) {
        __typename
        ... on CreateInvestmentAchTransfer {
          id
          status 
        }
        ... on CreateInvestmentAchTransferError {
          errorType
        }
        ... on AnnualRetirementContributionExceededError {
          retirementContribution {
            annualContributionsLimit
            remainingAnnualLimit
          }
        }
      }
    }`,
    },
    {
      createInvestmentAchTransferInput,
    },
    context,
  )) as { createInvestmentAchTransfer: CreateInvestmentAchTransferOutput }
  const result = response.createInvestmentAchTransfer

  // Invalidate investment account update queries if queryClient is provided and the transfer was successful
  if (queryClient && result.__typename === 'CreateInvestmentAchTransfer') {
    invalidateInvestmentAccountUpdateQueries({
      client: queryClient,
      investmentAccountAlias: createInvestmentAchTransferInput.investmentAccountAlias.alias,
      refetchType: 'all',
    })
  }

  return result
}

export const useCreateInvestmentAchTransfer = createMutationHook<
  CreateInvestmentAchTransferInput,
  CreateInvestmentAchTransferOutput
>(createInvestmentAchTransfer)

/**
 * Submit investment wire transfer
 * @param submitInvestmentWireInput - input for submitting an investment wire transfer
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns SubmitInvestmentWireSuccess | SubmitInvestmentWireOutputError
 *
 */
export const submitInvestmentWire = async (
  submitInvestmentWireInput: SubmitInvestmentWireInput,
  context?: DefaultContext,
): Promise<SubmitInvestmentWireOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitInvestmentWire',
      query: `mutation submitInvestmentWire($submitInvestmentWireInput: SubmitInvestmentWireInput!) {
      submitInvestmentWire(submitInvestmentWireInput: $submitInvestmentWireInput) {
        __typename
        ... on SubmitInvestmentWireSuccess {
          id
        }
        ... on SubmitInvestmentWireOutputError {
          errorType
          message
        }
      }
    }`,
    },
    {
      submitInvestmentWireInput,
    },
    context,
  )) as { submitInvestmentWire: SubmitInvestmentWireOutput }
  return response.submitInvestmentWire
}

export const useSubmitInvestmentWire = createMutationHook<SubmitInvestmentWireInput, SubmitInvestmentWireOutput>(
  submitInvestmentWire,
)
